import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Send, Clock, User, Shield, Lock, Paperclip, Upload, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusLabels = {
  recebida: { label: "Recebida", color: "bg-blue-100 text-blue-800" },
  em_analise: { label: "Em Análise", color: "bg-yellow-100 text-yellow-800" },
  investigacao: { label: "Investigação em Andamento", color: "bg-purple-100 text-purple-800" },
  concluida: { label: "Concluída", color: "bg-green-100 text-green-800" },
  arquivada: { label: "Arquivada", color: "bg-slate-100 text-slate-800" },
};

export default function DenunciaAcompanhamento() {
  const [protocolo, setProtocolo] = useState("");
  const [senha, setSenha] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (data?.mensagens) {
      scrollToBottom();
    }
  }, [data?.mensagens]);

  useEffect(() => {
    if (authenticated && protocolo && senha) {
      fetchAnexos();
    }
  }, [authenticated]);

  const fetchAnexos = async () => {
    try {
      const response = await axios.get(`${API_URL}/denuncias/acompanhar/anexos?protocolo=${protocolo}&senha=${senha}`);
      setAnexos(response.data);
    } catch (error) {
      console.error("Error fetching anexos:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append("protocolo", protocolo);
    formData.append("senha", senha);
    formData.append("file", file);

    try {
      await axios.post(`${API_URL}/denuncias/acompanhar/anexo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Arquivo anexado com sucesso!");
      fetchAnexos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao anexar arquivo");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadAnexo = async (anexoId, filename) => {
    try {
      toast.info("Preparando download...");
      const url = `${API_URL}/denuncias/anexo/${anexoId}?protocolo=${encodeURIComponent(protocolo)}&senha=${encodeURIComponent(senha)}`;
      const response = await axios.get(url, { responseType: "blob" });
      
      // Create blob link to download
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename || "anexo");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading anexo:", error);
      toast.error("Erro ao baixar anexo. O arquivo pode não existir no servidor.");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/denuncias/acompanhar`, {
        protocolo,
        senha,
      });
      setData(response.data);
      setAuthenticated(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Protocolo ou senha inválidos");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/denuncias/acompanhar/mensagem`, {
        protocolo,
        senha,
        conteudo: newMessage,
      });
      
      // Reload data
      const response = await axios.post(`${API_URL}/denuncias/acompanhar`, {
        protocolo,
        senha,
      });
      setData(response.data);
      setNewMessage("");
      toast.success("Mensagem enviada!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar mensagem");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        {/* Header */}
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#1a1a2e] transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar</span>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-[#1a1a2e]">CONTERP</span>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-md mx-auto px-6 py-16">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1a1a2e] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1a1a2e] mb-2">
              Acompanhar Denúncia
            </h1>
            <p className="text-slate-600">
              Digite seu protocolo e senha para acessar
            </p>
          </div>

          <form onSubmit={handleLogin} className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Número do Protocolo
                </label>
                <Input
                  value={protocolo}
                  onChange={(e) => setProtocolo(e.target.value.toUpperCase())}
                  placeholder="Ex: CON202401010001"
                  className="font-mono"
                  data-testid="protocolo-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Senha
                </label>
                <Input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value.toUpperCase())}
                  placeholder="Digite sua senha"
                  className="font-mono"
                  data-testid="senha-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-6 bg-[#1a1a2e] hover:bg-[#1a1a2e]/90"
              disabled={loading || !protocolo || !senha}
              data-testid="btn-acessar"
            >
              {loading ? "Verificando..." : "Acessar"}
            </Button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setAuthenticated(false)}
                className="flex items-center gap-2 text-slate-600 hover:text-[#1a1a2e] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-semibold text-[#1a1a2e]">
                  Protocolo: {data?.protocolo}
                </h1>
                <p className="text-xs text-slate-500">
                  Registrada em {formatDate(data?.created_at)}
                </p>
              </div>
            </div>
            <Badge className={statusLabels[data?.status]?.color}>
              {statusLabels[data?.status]?.label}
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Info Panel */}
          <div className="md:col-span-1 space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="font-semibold text-[#1a1a2e] mb-4">Informações</h2>
              <div className="space-y-4 text-sm">
                <div>
                  <span className="text-slate-500">Categoria</span>
                  <p className="text-[#1a1a2e] font-medium capitalize">{data?.categoria}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <p className="text-[#1a1a2e] font-medium">{statusLabels[data?.status]?.label}</p>
                </div>
              </div>
            </div>

            {/* Anexos */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[#1a1a2e]">Anexos</h2>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.mp3,.mp4,.wav"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    data-testid="btn-upload-anexo"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {uploadingFile ? "..." : "Anexar"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {anexos.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhum anexo</p>
                )}
                {anexos.map((anexo) => (
                  <div
                    key={anexo.id}
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-sm"
                  >
                    <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="flex-1 truncate text-[#1a1a2e]">{anexo.filename}</span>
                    <button
                      onClick={() => downloadAnexo(anexo.id, anexo.filename)}
                      className="text-[#0EA5E9] hover:text-[#0EA5E9]/80"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Public Dispatches */}
            {data?.despachos?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h2 className="font-semibold text-[#1a1a2e] mb-4">Atualizações</h2>
                <div className="space-y-4">
                  {data.despachos.map((d, i) => (
                    <div key={i} className="border-l-2 border-[#f5a623] pl-4">
                      <p className="text-sm text-[#1a1a2e]">{d.conteudo}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(d.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Chat Area */}
          <div className="md:col-span-2 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1a1a2e]" />
                <h2 className="font-semibold text-[#1a1a2e]">Chat Anônimo</h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Comunique-se com os investigadores sem revelar sua identidade
              </p>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto min-h-[400px] max-h-[500px]">
              {data?.mensagens?.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <p>Nenhuma mensagem ainda.</p>
                  <p className="text-sm">Envie uma mensagem para se comunicar com os investigadores.</p>
                </div>
              )}
              
              {data?.mensagens?.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.remetente === "denunciante" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.remetente === "denunciante"
                        ? "bg-[#1a1a2e] text-white rounded-br-sm"
                        : "bg-slate-100 text-[#1a1a2e] rounded-bl-sm"
                    }`}
                  >
                    <p className="text-sm">{msg.conteudo}</p>
                    <p className={`text-xs mt-1 ${msg.remetente === "denunciante" ? "text-white/60" : "text-slate-500"}`}>
                      {msg.remetente === "denunciante" ? "Você" : "Investigador"} • {formatDate(msg.created_at)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200">
              <div className="flex gap-3">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Digite sua mensagem..."
                  className="flex-1 min-h-[60px] max-h-[120px]"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90 self-end"
                  data-testid="btn-enviar-msg"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
